import { NotificationService } from 'src/app/shared/services/notification.service';
import { inject, Component, OnInit, EventEmitter } from '@angular/core';
import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { UploadInput, UploaderOptions } from '../../../_models/primeng-uploads';
import { UploadFile } from '../../../_models/primeng-uploads';
import { AdminUserService } from './../services/adminuser.service';
import { EmployeeServices } from './../../employees/services/employee.services';
import { ValidationService } from '../../shared/services/validation.service';
import { BusinessServices } from './../../businesses/services/business.services';
import { UserCredential } from './../../users/models/user';
import { AdminUser, UserAddressPropertyDto } from './../models/adminuser';

import { BaseServices } from '../../kuba.services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'adminuser-edit',
  templateUrl: 'adminuser-edit.component.html',
  standalone: false
})
export class AdminUserEditComponent implements OnInit {
  //#region variable
  private readonly notifications = inject(NotificationService);
  adminUserEditForm: FormGroup;
  updatePasswordForm: FormGroup;
  isResetPasswordForm = false;
  userCredentials = new UserCredential();
  id = 0;
  userId: number;
  adminUser: AdminUser;
  uploadRequest: any;
  users: any;
  businessPropId: any;
  isFileChoosed = false;
  files: UploadFile[] = [];
  uploadInput: EventEmitter<UploadInput>;
  humanizeBytes: Function;
  dragOver: boolean;
  options: UploaderOptions;
  userImage: string;
  showPasswordResetDialog = false;
  passwordValidation = false;
  validationErrors: string[] = [];
  cachedUserName: string;
  usernameWarning: string | null = null;
  isNewUser = false;
  addressPropertyId: number;
  private subscriptions: Subscription[] = [];
  languageId = BaseServices.getLanguageId();
  //#endregion

  //#region constructor
  constructor(
    private fb: FormBuilder,
    private userService: AdminUserService,
    private route: ActivatedRoute,
    public businessServices: BusinessServices,
    public employeeServices: EmployeeServices
  ) {
    this.id = this.route.snapshot.params['uid'];
  }
  //#endregion

  //#region region page-event
  ngOnInit() {
    document.querySelector('body').classList.remove('opened');
    this.uploadInput = new EventEmitter<UploadInput>();
    this.adminUserEditForm = this.fb.group({
      Name: [
        '',
        [Validators.required, ValidationService.noWhitespaceValidator]
      ],
      Username: [
        '',
        [Validators.required, ValidationService.noWhitespaceValidator],
        this.isUserNameUnique.bind(this)
      ],
      Email: ['', [Validators.required, Validators.email]],
      Mobile: ['', Validators.required],
      Address: [''],
      ZipCode: [''],
      PostalAddress: [''],
      Location: [''],
      PhoneCode: [2],
      UserType: [''],
      NewsLetter: [''],
      newPasswordValidation: this.fb.group({
        Password: [''],
        ConfirmPassword: ['']
      })
    });
    this.updatePasswordForm = this.fb.group({
      passwordValidation: this.fb.group({
        Password: ['', [Validators.required]],
        ConfirmPassword: ['', [Validators.required]]
      })
    });

    this.userService.checkNewUser(false);
    let userData = this.route.snapshot.data['userData'];
    if (this.id) {
      if (userData) {
        this.addressPropertyId = userData ? userData.AddressId : 0;
        this.cachedUserName = userData.User.Username;
        this.userImage = userData.User.Avatar ? userData.User.Avatar : null;
        this.userService.getExistingImage(this.userImage);
        this.adminUserEditForm = this.fb.group({
          Name: [userData.User.Name, [Validators.required, ValidationService.noWhitespaceValidator]],
          Username: [
            userData.User.Username,
            [Validators.required, ValidationService.noWhitespaceValidator],
            this.isUserNameUnique.bind(this)
          ],
          Email: [userData.User.Email, [Validators.required, Validators.email]],
          Mobile: [userData.User.Mobile, Validators.required],
          PhoneCode: userData.User ? parseInt(userData.User.CountryCode) : 47,
          UserType: userData.User.RoleId,
          Address: userData.User.Address,
          ZipCode: userData.User.Zip,
          IsTOCApproved: userData.User.IsTOCApproved,
          PostalAddress: userData.User.PostalAddress
            ? userData.User.PostalAddress
            : '',
          Location: userData.User.Location ? userData.User.Location : ''
        });
      }
      this.businessPropId = userData ? userData.AddressId : 0;
    }
    this.userService.getImage$.subscribe(imagePath => {
      this.userImage = imagePath ? imagePath : '';
    });
  }
  //#endregion

  //#region methods
  saveChanges() {
    if (this.adminUserEditForm.invalid || this.adminUserEditForm.pending) {
      return;
    }
    this.subscriptions.push(
      this.route.params.subscribe((params: Params) => {
        let uid = params['uid'];
        this.id = +uid;
      })
    );
    let adminUser = new UserAddressPropertyDto();
    adminUser.UserId = this.id > 0 ? this.id : 0;
    adminUser.User.CreatedBy = BaseServices.UserId;
    adminUser.User.Name = this.adminUserEditForm.value.Name;
    adminUser.User.Email = this.adminUserEditForm.value.Email;
    adminUser.User.Username = this.adminUserEditForm.value.Username;
    adminUser.User.LanguageId = this.adminUserEditForm.value.LanguageId;
    adminUser.User.RoleId = 1;
    adminUser.User.ApplicationId = BaseServices.ApplicationId;
    adminUser.User.PostalAddress = this.adminUserEditForm.value.PostalAddress;
    adminUser.User.BusinessId = BaseServices.BusinessId;
    adminUser.User.Status = 1;
    adminUser.User.FeatureId = 4;
    adminUser.User.Mobile = this.adminUserEditForm.value.Mobile.toString();
    adminUser.User.CountryCode = this.adminUserEditForm.value.PhoneCode
      ? this.adminUserEditForm.value.PhoneCode.toString()
      : '2';
    adminUser.User.Id = this.id > 0 ? this.id : 0;
    adminUser.User.Address = this.adminUserEditForm.value.Address;
    adminUser.User.Zip = this.adminUserEditForm.value.ZipCode;
    adminUser.User.Location = this.adminUserEditForm.value.Location;
    adminUser.AddressId = this.addressPropertyId ? this.addressPropertyId : 0;
    adminUser.User.Avatar = this.userImage;
    adminUser.User.IsTOCApproved = this.adminUserEditForm.value.IsTOCApproved;
    if (this.userImage) {
      this.userService.getUserProfileImagePath(this.userImage);
    }
    this.subscriptions.push(
      this.userService
        .updateAdminUser(adminUser.UserId, adminUser)
        .subscribe(isAdminUpdated => {
          if (isAdminUpdated) {
            this.notifications.success('UI_NOTIFICATIONS.SAVED');
          }
        })
    );
  }

  /**
   * reset the form
   */
  resetForm() {
    this.id = 0;
    this.adminUserEditForm = this.fb.group({
      Name: '',
      Email: '',
      Avatar: '',
      newPasswordValidation: this.fb.group({
        Password: '',
        ConfirmPassword: ''
      }),
      Username: '',
      LanguageId: '',
      Status: '',
      Address: '',
      Mobile: '',
      PhoneCode: [47],
      PostalAddress: '',
      Location: '',
      ZipCode: '',
      NewsLetter: ''
    });
  }

  /**
   * show the reset password feild
   */
  showResetPassword() {
    this.isResetPasswordForm = false;
    this.showPasswordResetDialog = true;
  }

  /**
   * user name validation
   */
  isUserNameUnique(control: FormControl) {
    const q = new Promise(resolve => {
      setTimeout(() => {
        if (control.value === this.cachedUserName) {
          this.usernameWarning = null;
          resolve(null);
        } else {
          this.subscriptions.push(
            this.employeeServices.getUserNameStatus(control.value).subscribe(
              (status: string) => {
                this.usernameWarning = null;
                if (status === 'Active') {
                  this.usernameWarning = 'active';
                  resolve({ invalidUserName: true, usernameStatus: 'active' });
                } else if (status === 'Deleted') {
                  this.usernameWarning = 'deleted';
                  resolve(null);
                } else if (status === 'Inactive') {
                  this.usernameWarning = 'inactive';
                  resolve(null);
                } else {
                  resolve(null);
                }
              },
              () => {
                resolve({ invalidUserName: true, usernameStatus: 'active' });
              }
            )
          );
        }
      }, 1000);
    });
    return q;
  }

  /**
   * user name validation
   */
  isPasswordMatch() {
    const q = new Promise(resolve => {
      setTimeout(() => {
        {
          resolve({ invalidPassWord: true });
        }
      }, 1000);
    });
    return q;
  }

  /**
   * reset the password
   * @param isSendSms Toggle for sending the password by SMS
   */
  resetPassWord(isSendSms: boolean) {
    this.userCredentials.Id = this.id;
    this.userCredentials.Password =
      this.updatePasswordForm.value.passwordValidation.Password;
    this.userCredentials.Password =
      this.updatePasswordForm.value.passwordValidation.ConfirmPassword;

    this.validationErrors = [];
    if (isSendSms) {
      this.businessServices
        .updatePasswordAndSendSMS(this.userCredentials, this.languageId)
        .subscribe(
          result => {
            if (result === false) {
              this.passwordValidation = true;
            } else {
              this.clear();
              this.notifications.success('UI_NOTIFICATIONS.SAVED');
            }
          },
          (error: any) => {
            if (error.status === 422) {
              this.validationErrors = error.error?.FailedChecks || [];
            }
          }
        );
    } else {
      this.subscriptions.push(
        this.businessServices
          .updatePassword(this.userCredentials)
          .subscribe(
            result => {
              if (result === false) {
                this.passwordValidation = true;
              } else {
                this.notifications.success('UI_NOTIFICATIONS.SAVED');
                this.clear();
              }
            },
            (error: any) => {
              if (error.status === 422) {
                this.validationErrors = error.error?.FailedChecks || [];
              }
            }
          )
      );
    }
  }

  /**
   * clear the password
   */
  clear() {
    this.isResetPasswordForm = true;
    this.showPasswordResetDialog = false;
    this.validationErrors = [];
    this.updatePasswordForm.reset();
  }

  onPasswordChange(pwd: string) {
    this.updatePasswordForm.patchValue({
      passwordValidation: {
        Password: pwd,
        ConfirmPassword: pwd
      }
    });
  }

  /**
   * With any changes in validation by child component
   * @param valid boolen
   */
  onValidChange(valid: boolean) {
    const pwGroup = this.updatePasswordForm.get(
      'passwordValidation'
    ) as FormGroup;

    if (valid) {
      pwGroup.setErrors(null);
    } else {
      pwGroup.setErrors({ invalidPassword: true });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub, i) => {
      sub.unsubscribe();
    });
  }
  //#endregion
}
